"use client"

import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip"
import { Card } from "../ui/card"
import {
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Key,
  KeyRound,
  PauseCircle,
  MapPin,
  AlertTriangle,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Phone,
  Send,
  Package,
  Truck,
  Copy,
  Calendar,
  User,
  Building2,
  CreditCard,
  Smartphone,
  Zap,
  Eye,
  ArrowLeftRight,
  ShieldAlert,
} from "lucide-react"
import type { GroupedContract, SuspensionLineDto, PendingToSendLineDto, ContractSummary } from "../../types/suspensions"
import { formatCnpj, formatPhone, formatDate } from "../../lib/format-utils"
import { getSuspensionImpedimentoLabel, lineHasImpedimento } from "../../constants/suspensionOperationalReasons"
import { getContractStatusText, normalizeContractStatus } from "../../utils/contractStatusUtils"
import { EContractStatus } from "../../services/contracts/ContractService"

interface ContractsTableProps {
  contracts: GroupedContract[]
  showSuspensions: boolean
  showToSend: boolean
  expandedContracts: Set<string>
  onToggleExpand: (contractId: string) => void
  onViewContract: (contractId: string) => void
  onViewContractSummary: (contractId: string) => void
  onViewCredentials: (lineId: string, clientId: string) => void
  onMarkSuspended: (lineId: string) => void
  onEditImpedimento?: (lineId: string) => void
  onMarkFastchipEnviado?: (contractId: string, contractStatusEnum?: string | null) => void
  onViewSummary: (lineId: string) => void
  onViewAddress: (line: PendingToSendLineDto) => void
  isAdmin: boolean
  /** Contratos Fastchip: modal «Contrato vinculado ao FAST» (linhas + grupo + troca admin). */
  onOpenFastLinkedContract?: (contractId: string, chipCcidForHistory: string | null) => void
}

function getUrgencyInfo(line: SuspensionLineDto) {
  if (line.estaSuspensa) {
    return {
      label: "SUSPENSA",
      color: "bg-emerald-100 text-emerald-700 border-emerald-300",
      icon: CheckCircle2,
      borderColor: "border-l-emerald-500",
      priority: 5,
    }
  }

  // First suspension (never suspended) - HIGHEST PRIORITY
  if (line.dataSuspensao === null) {
    return {
      label: "1ª SUSPENSÃO",
      color: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300",
      icon: Zap,
      borderColor: "border-l-fuchsia-500",
      priority: 0,
    }
  }

  if (line.diasRestantes === null) {
    return {
      label: "SEM DATA",
      color: "bg-gray-100 text-gray-700 border-gray-300",
      icon: Clock,
      borderColor: "border-l-gray-400",
      priority: 4,
    }
  }
  if (line.diasRestantes <= 3) {
    return {
      label: "URGENTE",
      color: "bg-red-100 text-red-700 border-red-300",
      icon: AlertTriangle,
      borderColor: "border-l-red-500",
      priority: 1,
    }
  }
  if (line.diasRestantes <= 7) {
    return {
      label: "ALERTA",
      color: "bg-orange-100 text-orange-700 border-orange-300",
      icon: AlertCircle,
      borderColor: "border-l-orange-500",
      priority: 2,
    }
  }
  return {
    label: "NORMAL",
    color: "bg-yellow-100 text-yellow-700 border-yellow-300",
    icon: Clock,
    borderColor: "border-l-yellow-500",
    priority: 3,
  }
}

function getContractStatusBadgeColor(contractStatusEnum: string | undefined | null): string {
  const n = normalizeContractStatus(contractStatusEnum ?? undefined)
  if (n === EContractStatus.Ativo) {
    return "bg-green-100 text-green-700 border-green-300"
  }
  if (n === EContractStatus.AprovadoCQV) {
    return "bg-amber-100 text-amber-700 border-amber-300"
  }
  if (n === EContractStatus.VtmeCriado) {
    return "bg-blue-100 text-blue-700 border-blue-300"
  }
  if (n === EContractStatus.RecusadoCQV || n === EContractStatus.RecusadoQualidade || n === EContractStatus.RecusadoCVP || n === EContractStatus.TrocaDeCarteiraNaoAutorizada) {
    return "bg-red-100 text-red-700 border-red-300"
  }
  if (n === EContractStatus.TrocaDeCarteira || n === EContractStatus.TrocaDeCarteiraSolicitada) {
    return "bg-amber-100 text-amber-800 border-amber-300"
  }
  return "bg-gray-100 text-gray-700 border-gray-300"
}

function getContractStatusInfo(
  contractStatus: string | undefined,
  contractStatusEnum: string | undefined | null,
): {
  label: string
  color: string
} {
  const label = getContractStatusText(contractStatus ?? contractStatusEnum)
  const color = getContractStatusBadgeColor(contractStatusEnum)
  return { label, color }
}

function getContractFastchipBadge(suspensionLines: GroupedContract["lines"]): { label: string; className: string } | null {
  const lines = suspensionLines.map((l) => l.data as SuspensionLineDto)
  const withEnviado = lines.filter((l) => l.fastchipEnviadoEm)
  if (withEnviado.length > 0) {
    const latest = withEnviado.reduce((a, b) =>
      new Date(b.fastchipEnviadoEm!) > new Date(a.fastchipEnviadoEm!) ? b : a,
    )
    return {
      label: `FC Enviado: ${formatDate(latest.fastchipEnviadoEm!)}`,
      className: "bg-teal-100 text-teal-800 border-teal-300",
    }
  }
  if (lines.some((l) => l.fastchip && !l.fastchipEnviadoEm)) {
    return {
      label: "FC Pendente",
      className: "bg-orange-100 text-orange-800 border-orange-300",
    }
  }
  return null
}

function confirmIfNonOperational(line: SuspensionLineDto): boolean {
  if (line.eligibleForUrgencyMetrics) return true
  return window.confirm(
    `O contrato está no status: ${line.contractStatus}. Operações neste estágio podem ser inadequadas. Deseja continuar?`,
  )
}

function getShippingType(lineCount: number): { type: "SEDEX" | "PAC"; color: string; icon: typeof Truck } {
  if (lineCount >= 3) {
    return { type: "SEDEX", color: "bg-red-100 text-red-700 border-red-300", icon: Truck }
  }
  return { type: "PAC", color: "bg-sky-100 text-sky-700 border-sky-300", icon: Package }
}

function getContractSummary(contract: GroupedContract): ContractSummary {
  const suspensions = contract.lines.filter((l) => l.type === "suspension")
  const toSend = contract.lines.filter((l) => l.type === "toSend")

  let maxUrgency: "firstSuspension" | "urgent" | "alert" | "normal" | "noDate" | null = null
  let firstSuspension = 0,
    urgent = 0,
    alert = 0,
    normal = 0,
    noDate = 0,
    missingCredentials = 0,
    alreadySuspended = 0,
    needsSuspension = 0,
    expiringSoon = 0

  suspensions.forEach((line) => {
    const data = line.data as SuspensionLineDto
    const eligible = data.eligibleForUrgencyMetrics

    if (!data.temCredenciais) missingCredentials++

    // Contar linhas já suspensas
    if (data.estaSuspensa || data.dataSuspensao !== null) {
      alreadySuspended++
    }

    // Contar linhas que precisam suspender (métricas de urgência)
    if (data.dataSuspensao === null && eligible) {
      needsSuspension++
    }

    // Contar linhas prestes a expirar
    if (eligible && data.diasRestantes !== null && data.diasRestantes <= 7) {
      expiringSoon++
    }

    // First suspension / buckets de urgência: apenas linhas elegíveis
    if (!eligible) return

    if (data.dataSuspensao === null) {
      firstSuspension++
      maxUrgency = "firstSuspension" // Highest priority
    } else if (data.diasRestantes === null) {
      noDate++
      if (!maxUrgency) maxUrgency = "noDate"
    } else if (data.diasRestantes <= 3) {
      urgent++
      if (maxUrgency !== "firstSuspension") maxUrgency = "urgent"
    } else if (data.diasRestantes <= 7) {
      alert++
      if (maxUrgency !== "firstSuspension" && maxUrgency !== "urgent") maxUrgency = "alert"
    } else {
      normal++
      if (!maxUrgency || maxUrgency === "noDate") maxUrgency = "normal"
    }
  })

  const statusCount: Record<string, number> = {}
  let withIccid = 0,
    withAddress = 0
  let mostRecentDate: string | null = null

  toSend.forEach((line) => {
    const data = line.data as PendingToSendLineDto
    if (data.chipCcid) withIccid++
    if (data.deliveryAddress) withAddress++
    statusCount[data.contractStatus] = (statusCount[data.contractStatus] || 0) + 1
    if (!mostRecentDate || data.dataCriacao > mostRecentDate) {
      mostRecentDate = data.dataCriacao
    }
  })

  const shippingType = toSend.length >= 3 ? "SEDEX" : "PAC"

  let headerBgColor = "bg-slate-50"
  let borderColor = "border-l-slate-300"

  if (suspensions.length > 0 && toSend.length === 0) {
    if (maxUrgency === "firstSuspension") {
      headerBgColor = "bg-fuchsia-50"
      borderColor = "border-l-fuchsia-500"
    } else if (maxUrgency === "urgent") {
      headerBgColor = "bg-red-50"
      borderColor = "border-l-red-500"
    } else if (maxUrgency === "alert") {
      headerBgColor = "bg-orange-50"
      borderColor = "border-l-orange-500"
    } else {
      headerBgColor = "bg-amber-50"
      borderColor = "border-l-amber-500"
    }
  } else if (toSend.length > 0 && suspensions.length === 0) {
    headerBgColor = "bg-violet-50"
    borderColor = "border-l-violet-500"
  } else if (suspensions.length > 0 && toSend.length > 0) {
    headerBgColor = "bg-gradient-to-r from-violet-50 to-orange-50"
    borderColor = "border-l-indigo-500"
  }

  return {
    suspensions: {
      total: suspensions.length,
      firstSuspension,
      urgent,
      alert,
      normal,
      noDate,
      missingCredentials,
      maxUrgency,
      alreadySuspended,
      needsSuspension,
      expiringSoon,
    },
    toSend: { total: toSend.length, withIccid, withAddress, statusCount, mostRecentDate, shippingType },
    headerBgColor,
    borderColor,
  }
}

function SuspensionQuickCard({
  line,
  onViewCredentials,
  onMarkSuspended,
  onEditImpedimento,
  onViewSummary,
  isAdmin,
}: {
  line: SuspensionLineDto
  onViewCredentials: (id: string, clientId: string) => void
  onMarkSuspended: (id: string) => void
  onEditImpedimento?: (id: string) => void
  onViewSummary: (id: string) => void
  isAdmin: boolean
}) {
  const hasImpedimento = lineHasImpedimento(line)
  const urgency = getUrgencyInfo(line)
  const isFirstSuspension = line.dataSuspensao === null && !hasImpedimento
  const isPending = !line.estaSuspensa
  const impedimentoLabel = isPending
    ? getSuspensionImpedimentoLabel(line.motivoOperacional, line.motivoOperacionalLabel)
    : null
  const statusInfo = getContractStatusInfo(line.contractStatus, line.contractStatusEnum)

  return (
    <Card
      className={`p-3 border-l-4 ${urgency.borderColor} hover:shadow-md transition-shadow cursor-pointer ${
        isFirstSuspension ? "ring-2 ring-fuchsia-300 ring-offset-1" : ""
      }`}
      onClick={() => onViewSummary(line.id)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className={`${urgency.color} text-xs font-semibold`}>
              <urgency.icon className="h-3 w-3 mr-1" />
              {urgency.label}
            </Badge>
            <Badge className={`${statusInfo.color} text-[10px]`}>
              {statusInfo.label}
            </Badge>
            {line.fastchip ? (
              <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-[10px]">Fastchip</Badge>
            ) : (
              <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-[10px]">Por Entrega</Badge>
            )}
            {line.trocaLinha && (
              <Badge className="bg-orange-500 text-white text-xs font-semibold">
                <ArrowLeftRight className="h-3 w-3 mr-1" />
                Troca de Linha
              </Badge>
            )}
            {!line.estaSuspensa && !isFirstSuspension && line.diasRestantes !== null && (
              <span
                className={`text-sm font-bold ${
                  line.diasRestantes <= 3
                    ? "text-red-600"
                    : line.diasRestantes <= 7
                      ? "text-orange-600"
                      : "text-yellow-600"
                }`}
              >
                {line.diasRestantes} dias restantes
              </span>
            )}
            {isPending && isFirstSuspension && (
              <span className="text-sm font-bold text-fuchsia-600">Precisa suspender agora!</span>
            )}
            {hasImpedimento && (
              <Badge className="bg-violet-100 text-violet-800 border-violet-300 text-[10px]">Com impedimento</Badge>
            )}
            {line.teamName && (
              <Badge variant="outline" className="text-slate-600 border-slate-300 text-[10px]">
                {line.teamName}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{line.numero ? formatPhone(line.ddd, line.numero) : "Sem número"}</span>
            <span className="text-muted-foreground">|</span>
            <span className="text-muted-foreground">{line.planName}</span>
          </div>

          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
            {impedimentoLabel && (
              <span className="text-violet-700 font-medium truncate max-w-[280px]">
                Impedimento: {impedimentoLabel}
              </span>
            )}
            {line.dataSuspensao && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{line.estaSuspensa ? "Suspensa em" : "Última"}: {formatDate(line.dataSuspensao)}</span>
              </div>
            )}
            {line.dataProximaSuspensao && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>Próxima: {formatDate(line.dataProximaSuspensao)}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2">
            {line.temCredenciais ? (
              <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-[10px]">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Credenciais OK
              </Badge>
            ) : (
              <Badge variant="outline" className="text-red-600 border-red-300 text-[10px]">
                <XCircle className="h-3 w-3 mr-1" />
                Sem credenciais
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {isAdmin && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant={line.temCredenciais ? "outline" : "destructive"}
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!confirmIfNonOperational(line)) return
                      onViewCredentials(line.id, line.clientId)
                    }}
                  >
                    {line.temCredenciais ? <Key className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{line.temCredenciais ? "Ver credenciais" : "Inserir credenciais"}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isPending && onEditImpedimento && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0 border-violet-300 text-violet-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!confirmIfNonOperational(line)) return
                      onEditImpedimento(line.id)
                    }}
                  >
                    <ShieldAlert className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Registar impedimento</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isPending && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="default"
                    className="h-8 w-8 p-0 bg-emerald-600 hover:bg-emerald-700"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!confirmIfNonOperational(line)) return
                      onMarkSuspended(line.id)
                    }}
                  >
                    <PauseCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Marcar como suspensa (+120 dias)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </Card>
  )
}

function ToSendQuickCard({
  line,
  shippingType,
  onViewContract,
  onViewAddress,
}: {
  line: PendingToSendLineDto
  shippingType: "SEDEX" | "PAC"
  onViewContract: (id: string) => void
  onViewAddress: (line: PendingToSendLineDto) => void
}) {
  const hasIccid = !!line.chipCcid
  const hasAddress = !!line.deliveryAddress
  const isComplete = hasIccid && hasAddress
  const shipping = getShippingType(shippingType === "SEDEX" ? 3 : 1)

  return (
    <Card
      className={`p-3 border-l-4 ${
        isComplete ? "border-l-emerald-500" : "border-l-orange-500"
      } hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className={`${shipping.color} text-xs font-semibold`}>
              <shipping.icon className="h-3 w-3 mr-1" />
              {shipping.type}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {line.contractStatus}
            </Badge>
            {line.teamName && (
              <Badge variant="outline" className="text-slate-600 border-slate-300 text-[10px]">
                {line.teamName}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{line.numero ? formatPhone(line.ddd, line.numero) : "Sem número"}</span>
            <span className="text-muted-foreground">|</span>
            <span className="text-muted-foreground">{line.planName}</span>
          </div>

          <div className="flex items-center gap-2 mt-2 text-xs">
            <Smartphone className="h-3 w-3 text-muted-foreground" />
            {hasIccid ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="font-mono text-emerald-600 cursor-pointer hover:underline">
                      {line.chipCcid!.slice(0, 8)}...{line.chipCcid!.slice(-4)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex items-center gap-2">
                      <span>{line.chipCcid}</span>
                      <Copy className="h-3 w-3" />
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="text-orange-600 font-medium">ICCID pendente</span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2">
            {hasIccid ? (
              <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-[10px]">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                ICCID
              </Badge>
            ) : (
              <Badge variant="outline" className="text-orange-600 border-orange-300 text-[10px]">
                <XCircle className="h-3 w-3 mr-1" />
                ICCID
              </Badge>
            )}
            {hasAddress ? (
              <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-[10px]">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Endereço
              </Badge>
            ) : (
              <Badge variant="outline" className="text-orange-600 border-orange-300 text-[10px]">
                <XCircle className="h-3 w-3 mr-1" />
                Endereço
              </Badge>
            )}
          </div>

          {hasAddress && line.deliveryAddress && (
            <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              <span className="truncate">
                {line.deliveryAddress.cidade}/{line.deliveryAddress.estado} - {line.deliveryAddress.cep}
              </span>
            </div>
          )}

          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(line.dataCriacao)}</span>
            </div>
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span>{line.createdBy}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 w-8 p-0 bg-transparent"
                  onClick={(e) => {
                    e.stopPropagation()
                    onViewContract(line.contractId)
                  }}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Ver contrato</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {hasAddress && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0 bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation()
                      onViewAddress(line)
                    }}
                  >
                    <MapPin className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ver endereço</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </Card>
  )
}

export function ContractsTable({
  contracts,
  showSuspensions,
  showToSend,
  expandedContracts,
  onToggleExpand,
  onViewContract,
  onViewContractSummary,
  onViewCredentials,
  onMarkSuspended,
  onEditImpedimento,
  onMarkFastchipEnviado,
  onViewSummary,
  onViewAddress,
  isAdmin,
  onOpenFastLinkedContract,
}: ContractsTableProps) {
  if (contracts.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">Nenhuma linha encontrada com os filtros selecionados.</p>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      {contracts.map((contract) => {
        const summary = getContractSummary(contract)
        const isExpanded = expandedContracts.has(contract.contractId)
        const shipping = getShippingType(summary.toSend.total)
        
        // Obter status do contrato (todas as linhas do mesmo contrato têm o mesmo status)
        const toSendStatus = contract.lines
          .filter((l) => l.type === "toSend")
          .map((l) => (l.data as PendingToSendLineDto).contractStatus)[0]
        const suspensionStatus = contract.lines
          .filter((l) => l.type === "suspension")
          .map((l) => (l.data as SuspensionLineDto).contractStatus)[0]
        const suspensionStatusEnum = contract.lines
          .filter((l) => l.type === "suspension")
          .map((l) => (l.data as SuspensionLineDto).contractStatusEnum)[0]
        const contractStatus = toSendStatus || suspensionStatus
        const statusInfo = getContractStatusInfo(contractStatus, suspensionStatusEnum)
        
        // Obter informações de data, consultor e team (priorizar toSend, mas usar suspension se não houver toSend)
        const firstToSendLine = contract.lines.find((l) => l.type === "toSend")?.data as PendingToSendLineDto | undefined
        const firstSuspensionLine = contract.lines.find((l) => l.type === "suspension")?.data as SuspensionLineDto | undefined
        const contractCreatedAt = firstToSendLine?.contractCreatedAt || firstSuspensionLine?.contractCreatedAt || null
        const contractApprovedAt = firstToSendLine?.contractApprovedAt || firstSuspensionLine?.contractApprovedAt || null
        const consultor = firstToSendLine?.createdBy || firstSuspensionLine?.createdBy || null
        const teamName = firstToSendLine?.teamName || firstSuspensionLine?.teamName || null

        const suspensionLines = contract.lines.filter((l) => l.type === "suspension")
        const toSendLines = contract.lines.filter((l) => l.type === "toSend")
        const fastchipHeaderBadge = getContractFastchipBadge(suspensionLines)
        // Identifica o tipo do contrato a partir das linhas (todas as linhas do mesmo
        // contrato compartilham `fastchip`, mas usamos a primeira que existir).
        const firstLineWithType =
          (suspensionLines[0]?.data as SuspensionLineDto | undefined) ??
          (toSendLines[0]?.data as PendingToSendLineDto | undefined)
        const contractIsFastchip = firstLineWithType?.fastchip ?? null
        const hasContractTrocaLinha = suspensionLines.some(
          (l) => (l.data as SuspensionLineDto).trocaLinha,
        )

        const hasFastchipPending =
          isAdmin &&
          !!onMarkFastchipEnviado &&
          suspensionLines.some((l) => (l.data as SuspensionLineDto).fastchip && !(l.data as SuspensionLineDto).fastchipEnviadoEm)
        const suspensionStatusEnumForFastchip = suspensionStatusEnum ?? null

        const chipForHistory =
          suspensionLines
            .map((l) => (l.data as SuspensionLineDto).chipCcid)
            .find((c) => !!c?.trim()) ??
          toSendLines.map((l) => (l.data as PendingToSendLineDto).chipCcid).find((c) => !!c?.trim()) ??
          null

        const sortedSuspensionLines = suspensionLines.sort((a, b) => {
          const aData = a.data as SuspensionLineDto
          const bData = b.data as SuspensionLineDto
          const aUrgency = getUrgencyInfo(aData)
          const bUrgency = getUrgencyInfo(bData)
          return aUrgency.priority - bUrgency.priority
        })

        return (
          <Card
            key={contract.contractId}
            id={`contract-card-${contract.contractId}`}
            className={`overflow-hidden border-l-4 ${summary.borderColor} ${
              summary.suspensions.firstSuspension > 0 ? "ring-2 ring-fuchsia-300 ring-offset-2" : ""
            }`}
          >
            {/* Contract Header */}
            <div
              className={`p-4 ${summary.headerBgColor} cursor-pointer hover:opacity-95 transition-opacity`}
              onClick={() => onToggleExpand(contract.contractId)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}

                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground">{contract.contractNumber}</span>
                      <div className="flex items-center gap-1">
                        {contractIsFastchip && onOpenFastLinkedContract && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation()
                              onOpenFastLinkedContract(contract.contractId, chipForHistory)
                            }}
                            title="Contrato FAST — linhas, grupo e troca (admin)"
                          >
                            <Smartphone className="h-3.5 w-3.5 text-blue-600" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation()
                            onViewContractSummary(contract.contractId)
                          }}
                          title="Ver resumo do contrato"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation()
                            onViewContract(contract.contractId)
                          }}
                          title="Ver contrato completo"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                        {hasFastchipPending && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-xs text-orange-700 border-orange-400 hover:bg-orange-50"
                            onClick={(e) => {
                              e.stopPropagation()
                              onMarkFastchipEnviado!(contract.contractId, suspensionStatusEnumForFastchip)
                            }}
                            title="Marcar Fast Chip como enviado"
                          >
                            <Package className="h-3 w-3 mr-1" />
                            Chip enviado
                          </Button>
                        )}
                      </div>

                      {/* Badges - Apenas informações essenciais */}
                      
                      {/* Status do Contrato */}
                      {(contractStatus || suspensionStatusEnum) && (
                        <Badge className={statusInfo.color}>
                          {statusInfo.label}
                        </Badge>
                      )}

                      {/* Tipo do contrato (Fastchip vs Por Entrega) */}
                      {contractIsFastchip !== null && (
                        contractIsFastchip ? (
                          <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-xs">
                            <Smartphone className="h-3 w-3 mr-1" />
                            Fastchip
                          </Badge>
                        ) : (
                          <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-xs">
                            <Truck className="h-3 w-3 mr-1" />
                            Por Entrega
                          </Badge>
                        )
                      )}

                      {fastchipHeaderBadge && (
                        <Badge variant="outline" className={`text-xs ${fastchipHeaderBadge.className}`}>
                          {fastchipHeaderBadge.label}
                        </Badge>
                      )}

                      {hasContractTrocaLinha && (
                        <Badge
                          variant="outline"
                          className="text-xs bg-amber-100 text-amber-900 border-amber-400 font-semibold"
                        >
                          <ArrowLeftRight className="h-3 w-3 mr-1" />
                          Troca de Linha
                        </Badge>
                      )}

                      {/* Linhas a Enviar */}
                      {summary.toSend.total > 0 && showToSend && (
                        <>
                          <Badge variant="outline" className="text-violet-600 border-violet-300">
                            <Send className="h-3 w-3 mr-1" />
                            {summary.toSend.total} envio{summary.toSend.total === 1 ? "" : "s"}
                          </Badge>
                          <Badge className={shipping.color}>
                            <shipping.icon className="h-3 w-3 mr-1" />
                            {shipping.type}
                          </Badge>
                        </>
                      )}

                      {/* Linhas a Suspender - Apenas badge mais prioritário */}
                      {summary.suspensions.total > 0 && showSuspensions && (
                        <>
                          {summary.suspensions.alreadySuspended > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              {summary.suspensions.alreadySuspended}/{summary.suspensions.total} suspensa
                              {summary.suspensions.alreadySuspended === 1 ? "" : "s"}
                            </Badge>
                          )}
                          {summary.suspensions.firstSuspension > 0 ? (
                            <Badge className="bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300 animate-pulse">
                              <Zap className="h-3 w-3 mr-1" />
                              {summary.suspensions.firstSuspension} 1ª SUSPENSÃO
                            </Badge>
                          ) : summary.suspensions.urgent > 0 ? (
                            <Badge className="bg-red-100 text-red-700 border-red-300">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {summary.suspensions.urgent} urgente{summary.suspensions.urgent === 1 ? "" : "s"}
                            </Badge>
                          ) : summary.suspensions.alert > 0 ? (
                            <Badge className="bg-orange-100 text-orange-700 border-orange-300">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              {summary.suspensions.alert} alerta{summary.suspensions.alert === 1 ? "" : "s"}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              <PauseCircle className="h-3 w-3 mr-1" />
                              {summary.suspensions.total} suspensã{summary.suspensions.total === 1 ? "o" : "ões"}
                            </Badge>
                          )}
                          
                          {/* Sem Credenciais - sempre mostra se houver */}
                          {summary.suspensions.missingCredentials > 0 && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              <KeyRound className="h-3 w-3 mr-1" />
                              {summary.suspensions.missingCredentials} sem acesso
                            </Badge>
                          )}
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground flex-wrap">
                      <Building2 className="h-3.5 w-3.5" />
                      <span>{contract.clientName}</span>
                      <span>|</span>
                      <CreditCard className="h-3.5 w-3.5" />
                      <span>{formatCnpj(contract.clientCnpj)}</span>
                      {contractCreatedAt && (
                        <>
                          <span>|</span>
                          <Calendar className="h-3.5 w-3.5" />
                          <span>Criado: {formatDate(contractCreatedAt)}</span>
                        </>
                      )}
                      {contractApprovedAt && (
                        <>
                          <span>|</span>
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                          <span>Aprovado: {formatDate(contractApprovedAt)}</span>
                        </>
                      )}
                      {consultor && (
                        <>
                          <span>|</span>
                          <User className="h-3.5 w-3.5" />
                          <span>{consultor}</span>
                        </>
                      )}
                      {teamName && (
                        <>
                          <span>|</span>
                          <span className="font-medium">{teamName}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm">
                  {summary.toSend.total > 0 && showToSend && (
                    <div className="flex items-center gap-2">
                      {summary.toSend.withIccid === summary.toSend.total ? (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          ICCIDs OK
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                          {summary.toSend.withIccid}/{summary.toSend.total} ICCIDs
                        </Badge>
                      )}
                      {summary.toSend.withAddress === summary.toSend.total ? (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Endereço OK
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
                          {summary.toSend.withAddress}/{summary.toSend.total} Endereços
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="p-4 bg-white border-t space-y-4">
                {(contractCreatedAt || contractApprovedAt) && (
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    {contractCreatedAt && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        Criação do contrato: {formatDate(contractCreatedAt)}
                      </span>
                    )}
                    {contractApprovedAt && (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Aprovação (Ativo): {formatDate(contractApprovedAt)}
                      </span>
                    )}
                  </div>
                )}
                {/* Suspensions Section */}
                {sortedSuspensionLines.length > 0 && showSuspensions && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-orange-700 flex items-center gap-2">
                      <PauseCircle className="h-4 w-4" />
                      Linhas a Suspender ({sortedSuspensionLines.length})
                    </h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {sortedSuspensionLines.map((line) => (
                        <SuspensionQuickCard
                          key={line.id}
                          line={line.data as SuspensionLineDto}
                          onViewCredentials={onViewCredentials}
                          onMarkSuspended={onMarkSuspended}
                          onEditImpedimento={onEditImpedimento}
                          onViewSummary={onViewSummary}
                          isAdmin={isAdmin}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* To Send Section */}
                {toSendLines.length > 0 && showToSend && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-violet-700 flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Linhas a Enviar ({toSendLines.length})
                      <Badge className={shipping.color}>
                        <shipping.icon className="h-3 w-3 mr-1" />
                        {shipping.type}
                      </Badge>
                    </h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {toSendLines.map((line) => (
                        <ToSendQuickCard
                          key={line.id}
                          line={line.data as PendingToSendLineDto}
                          shippingType={summary.toSend.shippingType}
                          onViewContract={onViewContract}
                          onViewAddress={onViewAddress}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        )
      })}
    </div>
  )
}
